/**
 * Shared styles for the token-authorized portals (E4b Items 2–5).
 *
 * ONE stylesheet for the work order, contractor estimate, invoice and inventory
 * views. They are the same document family in the same navy/amber branding, so a
 * per-view copy would drift on the first tweak.
 *
 * PHONE-FIRST. Every multi-column block is a grid that collapses to one column
 * under 620px, tables scroll inside their own container rather than pushing the
 * page sideways, and the invoice's balance sits in the header so it is readable
 * without scrolling.
 *
 * The print block strips the chrome (buttons, tab bar) so the printed page is
 * the document itself. P4 made this load-bearing rather than incidental: the
 * server generates PDFs by pointing headless Chromium at this very page with
 * emulateMediaType('print'), so THESE rules are what the PDF obeys. Changing
 * them changes the PDF.
 */

export const PORTAL_CSS = `
/* Declared HERE as well as in the document's own stylesheet, on purpose. The
   document CSS arrives inside rendered_content, which is a SNAPSHOT written at
   generation time — so a document generated before this face existed carries a
   stylesheet without it, and the adopt dialog would fall through to cursive on
   exactly the documents a signer is most likely to be looking at. Two identical
   declarations of the same family from the same URL cost one request. */
@font-face {
  font-family:'BVL Signature';
  src:url('/fonts/DancingScript-Variable.ttf') format('truetype');
  font-weight:400 700; font-style:normal; font-display:block;
}
.pp {
  --script:'BVL Signature','Dancing Script',cursive;
  --navy:#1a2b4a; --navy-deep:#16233d;
  --amber:#f5a623; --amber-deep:#b26f00; --amber-bg:#fdf3e0;
  --bg:#f4f5f7; --card:#ffffff; --tint:#f7f8fa;
  --ink:#1f2733; --soft:#5b6473; --faint:#8b93a1; --line:#e3e6ec;
  --good:#0f8a4c; --good-bg:#e7f6ee;
  min-height:100vh; background:var(--bg); color:var(--ink);
  font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
  line-height:1.55; -webkit-font-smoothing:antialiased;
}
.pp * { box-sizing:border-box; }

/* ---- shell ---- */
.pp-bar { background:var(--navy); color:#fff; }
.pp-bar-in { max-width:760px; margin:0 auto; padding:14px 18px; display:flex; align-items:center; justify-content:space-between; gap:12px; }
/* The logo: white on the navy bar on screen; the print bar is white, so the
   colour version replaces it on paper (PDFs print with print media). The alt
   text is the company name, so a blocked image still reads as the business. */
.pp-logo { height:34px; width:auto; display:block; }
.pp-logo-print { display:none; }
.pp-tel { color:var(--amber); font-size:14px; font-weight:700; text-decoration:none; white-space:nowrap; }
.pp-main { max-width:760px; margin:0 auto; padding:22px 16px 56px; }
.pp-mid { max-width:520px; margin:64px auto; padding:0 20px; text-align:center; color:var(--soft); }
.pp-mid h2 { color:var(--navy); margin:0 0 8px; }
.pp-foot { margin-top:26px; padding-top:18px; border-top:1px solid var(--line); text-align:center; font-size:12px; color:var(--faint); }
.pp-foot a { color:var(--soft); text-decoration:none; }
/* D2: the generated document brings its own stylesheet; give it room and do not
   restyle it here — a second opinion about its typography is how the portal and
   the PDF drift apart. */
.pp-doc-embed .sheet { max-width:none; margin:0; }
/* P4: inline PDF failure, beside the button — never an alert() on a client page. */
.pp-pdf-error { margin-top:10px; font-size:13px; color:#b42318; background:#fef3f2; border:1px solid #fda29b; border-radius:8px; padding:10px 12px; }

/* ---- document header ---- */
.pp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.pp-kicker { font-size:11px; text-transform:uppercase; letter-spacing:1.2px; color:var(--amber-deep); font-weight:800; }
.pp-h1 { font-size:26px; color:var(--navy); margin:2px 0 0; font-weight:800; line-height:1.15; }
.pp-jobno { font-family:'DM Mono',ui-monospace,monospace; font-size:15px; color:var(--soft); margin-top:2px; }
.pp-badge { display:inline-block; font-size:10px; font-weight:800; letter-spacing:.6px; text-transform:uppercase; padding:4px 10px; border-radius:999px; background:#eef1f6; color:var(--navy); }
.pp-badge.ok { background:var(--good-bg); color:var(--good); }
.pp-badge.warn { background:var(--amber-bg); color:var(--amber-deep); }

/* ---- cards + sections ---- */
.pp-card { background:var(--card); border:1px solid var(--line); border-radius:12px; padding:18px; margin-bottom:14px; box-shadow:0 1px 3px rgba(22,35,61,.04); }
.pp-card.accent { border-color:var(--amber); border-width:2px; }
.pp-sec { font-size:11px; text-transform:uppercase; letter-spacing:1px; color:var(--amber-deep); font-weight:800; margin:0 0 12px; }
.pp-sub { font-size:13px; color:var(--soft); }

/* ---- tiles ---- */
.pp-tiles { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.pp-tiles.four { grid-template-columns:repeat(4,1fr); }
.pp-tile { background:var(--tint); border:1px solid var(--line); border-radius:9px; padding:10px 12px; min-width:0; }
.pp-tile-l { font-size:10px; text-transform:uppercase; letter-spacing:.6px; color:var(--faint); font-weight:700; margin-bottom:3px; }
.pp-tile-v { font-size:15px; font-weight:700; color:var(--navy); overflow-wrap:anywhere; }
.pp-tile-v.sm { font-size:13px; font-weight:600; }
/* AMBER = a condition the crew must plan for. Deliberately rare. */
.pp-tile.alert { background:var(--amber-bg); border-color:var(--amber); }
.pp-tile.alert .pp-tile-l { color:var(--amber-deep); }
.pp-tile.alert .pp-tile-v { color:var(--amber-deep); }

/* ---- pickup / delivery pair ---- */
.pp-legs { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.pp-leg { background:var(--card); border:1px solid var(--line); border-radius:12px; padding:18px; box-shadow:0 1px 3px rgba(22,35,61,.04); min-width:0; }
.pp-when { font-size:15px; font-weight:800; color:var(--amber-deep); margin-bottom:10px; }
.pp-addr { font-size:17px; font-weight:800; color:var(--navy); line-height:1.3; overflow-wrap:anywhere; }
.pp-addr-sub { font-size:13px; color:var(--soft); margin-top:2px; overflow-wrap:anywhere; }

/* ---- tables ---- */
.pp-wrap { overflow-x:auto; }
.pp-tbl { width:100%; border-collapse:collapse; font-size:14px; }
.pp-tbl td { padding:10px 0; border-bottom:1px solid #eef0f3; vertical-align:top; }
.pp-tbl tr:last-child td { border-bottom:none; }
.pp-rule { font-size:12px; color:var(--soft); font-family:'DM Mono',ui-monospace,monospace; margin-top:2px; }
.pp-amt { text-align:right; font-family:'DM Mono',ui-monospace,monospace; white-space:nowrap; padding-left:12px; }
.pp-tot td { border-top:2px solid var(--navy); border-bottom:none; font-weight:800; font-size:16px; color:var(--navy); padding-top:12px; }

/* ---- totals block (right-aligned) ---- */
.pp-totals { margin-left:auto; max-width:340px; margin-top:14px; }
.pp-trow { display:flex; justify-content:space-between; gap:16px; padding:5px 0; font-size:14px; }
.pp-trow span:last-child { font-family:'DM Mono',ui-monospace,monospace; white-space:nowrap; }
.pp-trow.grand { border-top:2px solid var(--navy); margin-top:6px; padding-top:11px; font-size:18px; font-weight:800; color:var(--navy); }

/* ---- balance / paid ---- */
.pp-balance { text-align:right; }
.pp-balance-l { font-size:11px; text-transform:uppercase; letter-spacing:.8px; color:var(--faint); font-weight:700; }
.pp-balance-v { font-size:30px; font-weight:800; color:var(--navy); font-family:'DM Mono',ui-monospace,monospace; line-height:1.1; }
.pp-paid { display:inline-block; background:var(--good); color:#fff; font-weight:800; letter-spacing:1.5px; font-size:16px; padding:8px 18px; border-radius:8px; }

/* ---- callout ---- */
.pp-callout { display:flex; gap:11px; align-items:flex-start; background:var(--amber-bg); border:1px solid var(--amber); border-left-width:4px; border-radius:9px; padding:13px 15px; }
.pp-callout-t { font-weight:800; color:var(--navy); font-size:13px; margin-bottom:2px; }
.pp-callout-b { font-size:13px; color:var(--ink); }

/* ---- terms ---- */
.pp-terms { list-style:none; margin:0; padding:0; display:grid; gap:9px; }
.pp-terms li { border-left:3px solid var(--amber); padding:1px 0 1px 12px; font-size:13px; color:var(--soft); }

/* ---- buttons ---- */
.pp-acts { display:flex; gap:10px; flex-wrap:wrap; margin-top:16px; }
.pp-btn { flex:1 1 190px; display:inline-flex; align-items:center; justify-content:center; gap:7px; padding:13px 18px; border-radius:9px; font-size:15px; font-weight:700; cursor:pointer; border:1px solid var(--line); background:var(--card); color:var(--navy); text-decoration:none; text-align:center; }
.pp-btn:hover { background:var(--tint); }
.pp-btn.primary { background:var(--amber); border-color:var(--amber); color:#3a2a06; box-shadow:0 2px 8px rgba(245,166,35,.35); }
.pp-btn.primary:hover { filter:brightness(1.03); }
.pp-btn:disabled { opacity:.6; cursor:default; }
.pp-done { display:flex; align-items:center; justify-content:center; gap:8px; background:var(--good-bg); color:var(--good); font-weight:800; padding:13px 18px; border-radius:9px; flex:1 1 190px; }

/* ---- inventory ---- */
.pp-inv-row { display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
.pp-room { border:1px solid var(--line); border-radius:10px; margin-bottom:10px; overflow:hidden; }
.pp-room-h { background:var(--tint); padding:9px 13px; display:flex; justify-content:space-between; gap:12px; align-items:baseline; }
.pp-room-n { font-weight:800; color:var(--navy); font-size:14px; }
.pp-room-m { font-size:12px; color:var(--soft); font-family:'DM Mono',ui-monospace,monospace; white-space:nowrap; }
.pp-room-b { padding:4px 13px 8px; }
.pp-qty { font-family:'DM Mono',ui-monospace,monospace; color:var(--soft); white-space:nowrap; padding-left:12px; text-align:right; }

@media (max-width:620px) {
  .pp-legs { grid-template-columns:1fr; }
  .pp-tiles, .pp-tiles.four { grid-template-columns:1fr 1fr; }
  .pp-h1 { font-size:22px; }
  .pp-balance { text-align:left; }
  .pp-balance-v { font-size:26px; }
  .pp-card, .pp-leg { padding:15px; }
  .pp-btn { flex:1 1 100%; }
  /* THE STICKY BAR ON A PHONE, where the scrolling problem it solves is worst
     and the space to solve it in is smallest. The action drops to its own
     full-width row, under a consequence line that now reads left like the rest
     of the column. */
  /* PREFIXED WITH .pp TO OUT-SPECIFY, NOT TO SCOPE. This media block sits
     ABOVE the .pp-progress base rules in this file, so a bare .pp-progress-act
     here loses to the identical selector below on source order alone and the
     override silently does nothing. The extra class buys the specificity that
     position in the file does not. */
  .pp .pp-progress-act { flex:1 1 100%; margin-left:0; }
  .pp .pp-progress-act .pp-btn { width:100%; }
  .pp .pp-progress-final { text-align:left; max-width:none; margin-left:0; }
  /* THE LIST IS CAPPED, NOT DROPPED. Unbounded growth is the danger — a sticky
     element that gets taller with every unsigned block will eventually own the
     screen — and a cap ends that while keeping what the list is for. Past the
     cap, .pp-remaining-more says how many are behind it.

     n+4 IS REMAINING_CAP + 1 (SigningView). The two have to agree: this rule
     decides what is hidden, that constant decides what is counted. */
  .pp .pp-progress .pp-remaining li:nth-child(n+4) { display:none; }
  .pp .pp-remaining-more { display:block; }
}

/* P4: long documents must paginate, not overflow. Cards and table rows are kept
   whole across a page break, and a table repeats nothing it shouldn't. */

/* ---- client inventory (P5a) ----
   The one portal page the client WRITES on, so every rule here is sized for a
   thumb on a phone: 44px minimum on anything tappable, and the grid reflows to
   two columns rather than shrinking the tiles.

   NOTHING BELOW RENDERS A NUMBER THE CLIENT SHOULD NOT SEE. There is no cuft
   or weight class in this block because there is no cuft or weight in the
   payload — see backend/src/lib/clientInventory.ts. */

/* path chooser */
.ci-choose { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
.ci-choice { display:flex; flex-direction:column; align-items:center; gap:8px; text-align:center;
  padding:22px 16px; border:1px solid var(--line); border-radius:12px; background:var(--card);
  cursor:pointer; font-family:inherit; color:var(--ink); }
.ci-choice:hover { border-color:var(--amber); background:var(--amber-bg); }
.ci-choice.on { border-color:var(--amber); border-width:2px; background:var(--amber-bg); }
.ci-choice-t { font-size:15px; font-weight:800; color:var(--navy); }
.ci-choice-b { font-size:12.5px; color:var(--soft); line-height:1.45; }
.ci-choice svg { color:var(--amber-deep); }

/* the capture-path toggle — all three paths, switchable until the list is sent */
.ci-toggle { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:7px; }
.ci-tog { display:flex; align-items:center; justify-content:center; gap:8px; min-height:44px;
  padding:11px 12px; border:1px solid var(--line); border-radius:10px; background:var(--card);
  cursor:pointer; font-family:inherit; font-size:13.5px; font-weight:700; color:var(--soft); }
.ci-tog:hover:not(:disabled) { border-color:var(--amber); }
/* 2px on the chosen one, so the state survives a screen read in daylight and
   does not rely on the amber tint alone. */
.ci-tog.on { border-color:var(--amber); border-width:2px; background:var(--amber-bg); color:var(--navy); }
.ci-tog svg { color:var(--amber-deep); }
.ci-tog:disabled { cursor:default; opacity:.65; }
.ci-toggle-h { font-size:12.5px; color:var(--faint); line-height:1.45; margin:0 0 14px; }

/* room accordion */
.ci-room { border:1px solid var(--line); border-radius:12px; margin-bottom:10px; overflow:hidden; background:var(--card); }
.ci-room.open { border-color:var(--amber); }
.ci-room-h { width:100%; display:flex; align-items:center; gap:10px; padding:13px 15px; min-height:52px;
  background:var(--tint); border:none; cursor:pointer; font-family:inherit; text-align:left; color:var(--navy); }
.ci-room-n { font-weight:800; font-size:14.5px; flex:1; }
.ci-room-c { font-size:12px; font-weight:700; color:var(--amber-deep); background:var(--amber-bg);
  border-radius:999px; padding:3px 9px; white-space:nowrap; }
.ci-room-b { padding:14px 15px 16px; }

/* item tiles */
.ci-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(104px,1fr)); gap:9px; }
.ci-tile { display:flex; flex-direction:column; align-items:center; gap:6px; padding:11px 7px 9px;
  border:1px solid var(--line); border-radius:10px; background:var(--card); cursor:pointer;
  font-family:inherit; color:var(--ink); min-height:112px; justify-content:center; }
.ci-tile:hover { border-color:var(--amber); }
.ci-tile.on { border-color:var(--amber); border-width:2px; background:var(--amber-bg); padding:10px 6px 8px; }
/* P5b-1: the artwork box is a fixed square (CatalogArt sizes it inline) so a
   grid of 40 tiles does not reflow as images arrive — a client scrolling with a
   thumb would otherwise tap the item that just moved under it. */
.ci-tile img { pointer-events:none; }
.ci-tile svg { color:var(--navy); }
.ci-tile.on svg { color:var(--amber-deep); }
.ci-tile-n { font-size:11.5px; line-height:1.25; text-align:center; font-weight:600; overflow-wrap:anywhere; }

/* quantity stepper — its own row so a mis-tap changes a count, never the item */
.ci-step { display:flex; align-items:center; gap:2px; }
.ci-step button { width:30px; height:30px; border-radius:7px; border:1px solid var(--amber);
  background:var(--card); color:var(--amber-deep); font-size:17px; font-weight:800; line-height:1;
  cursor:pointer; font-family:inherit; display:flex; align-items:center; justify-content:center; }
.ci-step button:disabled { opacity:.35; cursor:default; }
/* the number is typeable on every path ("50 boxes" is not fifty taps) */
.ci-step input { width:34px; height:30px; text-align:center; border:1px solid var(--line); border-radius:7px;
  background:#fff; color:var(--navy); font-size:16px; font-weight:800; padding:0;
  font-family:'DM Mono',ui-monospace,monospace; }
.ci-step input:focus { outline:2px solid var(--amber); outline-offset:1px; }
.ci-step button { touch-action:manipulation; }

/* a row whose save failed: the count stays, this says so, Retry sends it again */
.ci-unsaved { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-top:4px;
  font-size:12px; font-weight:700; color:#b42318; }
.ci-unsaved button { min-height:36px; padding:6px 12px; border-radius:8px; border:1px solid #b42318;
  background:#fff; color:#b42318; font-size:12.5px; font-weight:800; cursor:pointer; font-family:inherit; }
.ci-tile.bad, .ci-line.bad, .cs-row.bad { border-color:#b42318; }
.ci-tile .ci-unsaved { justify-content:center; text-align:center; }

.ci-step-v { min-width:26px; text-align:center; font-weight:800; font-size:14px; color:var(--navy);
  font-family:'DM Mono',ui-monospace,monospace; }

/* the flat list (cube-sheet path) and the read-only summary */
.ci-list { display:grid; gap:7px; }
.ci-line { display:flex; align-items:center; gap:11px; padding:9px 11px; border:1px solid var(--line);
  border-radius:9px; background:var(--card); }
.ci-line svg { color:var(--navy); flex:0 0 auto; }
.ci-line-m { flex:1; min-width:0; }
.ci-line-n { font-size:14px; font-weight:600; overflow-wrap:anywhere; }
.ci-line-s { font-size:12px; color:var(--soft); overflow-wrap:anywhere; margin-top:1px; }

/* ours, not theirs */
.ci-ours { display:inline-block; font-size:10px; font-weight:800; letter-spacing:.5px; text-transform:uppercase;
  color:var(--soft); background:var(--tint); border:1px solid var(--line); border-radius:999px;
  padding:2px 7px; margin-left:6px; vertical-align:1px; }

/* add-your-own */
.ci-add { display:flex; gap:8px; margin-top:12px; flex-wrap:wrap; }
.ci-add input { flex:1 1 180px; padding:11px 12px; border:1px solid var(--line); border-radius:8px;
  font-size:15px; font-family:inherit; color:var(--ink); background:#fff; }
.ci-add input:focus { outline:2px solid var(--amber); outline-offset:1px; }
.ci-add button { flex:0 0 auto; padding:11px 16px; border-radius:8px; border:1px solid var(--line);
  background:var(--tint); color:var(--navy); font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; }
.ci-add button:disabled { opacity:.5; cursor:default; }

/* search */
.ci-search { width:100%; padding:12px 13px; border:1px solid var(--line); border-radius:9px;
  font-size:15px; font-family:inherit; color:var(--ink); background:#fff; margin-bottom:12px; }
.ci-search:focus { outline:2px solid var(--amber); outline-offset:1px; }

/* notes */
.ci-note { margin-top:14px; }
.ci-note > span { display:block; font-size:12px; font-weight:700; color:var(--soft); margin-bottom:5px; }
.ci-note textarea { width:100%; padding:11px 12px; border:1px solid var(--line); border-radius:8px;
  font-size:15px; color:var(--ink); background:#fff; font-family:inherit; resize:vertical; min-height:74px; }
.ci-note textarea:focus { outline:2px solid var(--amber); outline-offset:1px; }
.ci-note-h { font-size:12px; color:var(--faint); margin-top:5px; }

/* P5b MEDIA SLOT — the section exists so the page's shape is already right when
   photos and video arrive. It renders from inventory.media, which the shaper
   pins to [] until that phase fills it. */
/* ---- photos and video (P5b) ----
   Tiles are a fixed grid so a half-loaded page does not reflow under a thumb,
   and every tap target clears 44px. */
.ci-media-acts { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:8px; }
.ci-media-h { font-size:12px; color:var(--faint); line-height:1.5; margin-bottom:4px; }

.ci-mgrid { display:grid; grid-template-columns:repeat(auto-fill,minmax(132px,1fr)); gap:10px; margin-top:12px; }
.ci-mtile { border:1px solid var(--line); border-radius:10px; overflow:hidden; background:var(--card);
  display:flex; flex-direction:column; }
.ci-mthumb { border:none; padding:0; margin:0; background:var(--tint); cursor:pointer; display:block;
  width:100%; aspect-ratio:4/3; overflow:hidden; }
.ci-mthumb img { width:100%; height:100%; object-fit:cover; display:block; }
.ci-mvid { width:100%; height:100%; display:flex; align-items:center; justify-content:center;
  color:var(--amber-deep); background:var(--amber-bg); }
.ci-mmeta { font-size:11px; color:var(--faint); padding:6px 8px 0; }
.ci-mcap { width:100%; border:none; border-top:1px solid var(--line); background:transparent;
  font-family:inherit; font-size:12px; color:var(--ink); padding:7px 8px; margin-top:6px; }
.ci-mcap::placeholder { color:var(--faint); }
.ci-mrm { border:none; border-top:1px solid var(--line); background:transparent; cursor:pointer;
  font-family:inherit; font-size:11.5px; font-weight:700; color:var(--soft); padding:8px; min-height:34px; }
.ci-mrm:hover { color:#b42318; }

/* in-progress uploads */
.ci-up { display:flex; align-items:center; gap:9px; margin-top:8px; font-size:12px; color:var(--soft); }
.ci-up-n { flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ci-up-t { flex:0 0 84px; height:6px; border-radius:999px; background:var(--tint); overflow:hidden; }
.ci-up-f { height:100%; background:var(--amber); transition:width .2s linear; }
.ci-up-p { flex:0 0 46px; text-align:right; font-variant-numeric:tabular-nums; }

/* full size, over the page */
.ci-lb { position:fixed; inset:0; z-index:60; background:rgba(16,24,40,.82);
  display:flex; align-items:center; justify-content:center; padding:16px; }
.ci-lb-in { background:var(--card); border-radius:12px; overflow:hidden; max-width:min(960px,100%);
  max-height:100%; display:flex; flex-direction:column; }
.ci-lb-m { max-width:100%; max-height:72vh; object-fit:contain; background:#000; display:block; }
.ci-lb-bar { display:flex; align-items:center; gap:10px; padding:11px 13px; flex-wrap:wrap; }
.ci-lb-c { flex:1 1 140px; font-size:12.5px; color:var(--soft); min-width:0;
  overflow:hidden; text-overflow:ellipsis; }

/* the saving/saved whisper — never a blocking spinner on a page someone is typing on */
.ci-state { font-size:12px; color:var(--faint); min-height:17px; }
.ci-state.err { color:#b42318; font-weight:700; }

/* submit bar */
.ci-bar { position:sticky; bottom:0; z-index:15; background:var(--card); border:1px solid var(--line);
  border-radius:12px; padding:13px 16px; margin-top:16px; box-shadow:0 -2px 12px rgba(22,35,61,.09); }
.ci-bar-c { font-size:12.5px; color:var(--soft); margin-bottom:9px; }
.ci-count { font-size:13px; color:var(--soft); font-weight:700; }

/* ---- the cube-sheet path (044) ----
   The whole catalogue by category, a stepper on every row. Search and the
   category jump bar stick to the top; the send bar sticks to the bottom. Every
   tap target is 44px. The rows are a list, not tiles: 170 tiles each carrying a
   stepper is a page nobody finishes on a phone. */
.cs-top { position:sticky; top:env(safe-area-inset-top,0px); z-index:16; background:var(--bg);
  margin:0 -4px 10px; padding:8px 4px 6px; }
.cs-top .ci-search { margin-bottom:8px; }
.cs-jump { display:flex; gap:6px; overflow-x:auto; scrollbar-width:none; -webkit-overflow-scrolling:touch;
  padding-bottom:2px; }
.cs-jump::-webkit-scrollbar { display:none; }
.cs-chip { flex:0 0 auto; display:inline-flex; align-items:center; gap:6px; min-height:40px;
  padding:8px 13px; border:1px solid var(--line); border-radius:999px; background:var(--card);
  font-family:inherit; font-size:13px; font-weight:700; color:var(--soft); cursor:pointer; white-space:nowrap; }
.cs-chip.on { border-color:var(--amber); border-width:2px; padding:7px 12px; background:var(--amber-bg); color:var(--navy); }
.cs-chip-c { font-size:11.5px; font-weight:800; color:var(--amber-deep); }

/* "Can't find it? Add '…' as your own item" — one tap, full width, 48px */
.ci-nomatch { display:flex; flex-direction:column; align-items:flex-start; gap:2px; width:100%; min-height:48px;
  padding:11px 14px; border:1px dashed var(--amber); border-radius:10px; background:var(--amber-bg);
  cursor:pointer; font-family:inherit; font-size:14px; font-weight:700; color:var(--navy); text-align:left; }
.ci-nomatch-t { font-size:12px; font-weight:600; color:var(--soft); }

/* Boxes / Misc — the whole-move section, first on every path */
.ci-whole { border-color:var(--amber); }

/* clears the sticky search + jump bar when a chip scrolls a section to the top */
.cs-sec { scroll-margin-top:120px; }
.cs-sec-h { display:flex; align-items:center; gap:9px; margin-bottom:10px; color:var(--navy); }
.cs-sec-h .pp-sec { margin:0; flex:1; }

.cs-rows { display:grid; gap:6px; }
.cs-row { display:flex; align-items:center; gap:11px; padding:7px 8px 7px 9px; min-height:62px;
  border:1px solid var(--line); border-radius:10px; background:var(--card); }
.cs-row.on { border-color:var(--amber); background:var(--amber-bg); }
.cs-row svg { color:var(--navy); flex:0 0 auto; }
.cs-row.on svg { color:var(--amber-deep); }
.cs-row-n { flex:1; min-width:0; font-size:14px; font-weight:600; line-height:1.3; overflow-wrap:anywhere; }

.cs-step { display:flex; align-items:center; gap:3px; flex:0 0 auto; }
.cs-step button { width:44px; height:44px; border-radius:9px; border:1px solid var(--amber);
  background:var(--card); color:var(--amber-deep); font-size:20px; font-weight:800; line-height:1;
  cursor:pointer; font-family:inherit; display:flex; align-items:center; justify-content:center;
  touch-action:manipulation; }
.cs-step button:disabled { opacity:.3; cursor:default; }
.cs-step input { width:46px; height:44px; text-align:center; border:1px solid var(--line); border-radius:9px;
  background:#fff; color:var(--navy); font-size:16px; font-weight:800; font-family:'DM Mono',ui-monospace,monospace; }
.cs-step input:focus { outline:2px solid var(--amber); outline-offset:1px; }

.cs-note-btn { margin-top:10px; padding:10px 2px; min-height:44px; border:none; background:none; cursor:pointer;
  font-family:inherit; font-size:13px; font-weight:700; color:var(--amber-deep); }

@media (max-width:620px) {
  .ci-choose { grid-template-columns:1fr; }
  .ci-grid { grid-template-columns:repeat(auto-fill,minmax(96px,1fr)); gap:8px; }
  /* three across at 400px: icon over label, so "Room by room" still fits */
  .ci-tog { flex-direction:column; gap:4px; padding:9px 6px; font-size:12.5px; text-align:center; line-height:1.2; }
  .cs-row { gap:9px; }
  .cs-row-n { font-size:13.5px; }
}

/* ---- signing (D1 Item 4) ----
   The controls sit INSIDE the document, on the ruled lines where the mark
   actually goes. Everything here is chrome around the document's own
   stylesheet — no rule below restyles .sig-rule, .init-box or .choicebox, so
   the page and the PDF cannot disagree about the document itself. */
.pp-blk { margin-top:8px; }
.pp-blk-btn { display:inline-flex; align-items:center; gap:8px; padding:11px 16px; border-radius:8px;
  border:1px solid var(--amber); background:var(--amber); color:#3a2a06; font-size:14px; font-weight:700;
  cursor:pointer; box-shadow:0 2px 8px rgba(245,166,35,.3); font-family:inherit; }
.pp-blk-btn:disabled { opacity:.6; cursor:default; }
/* The NEXT thing to do pulses gently. On a long contract the signer's problem is
   finding the field, not deciding whether to sign it. */
.pp-blk.next .pp-blk-btn { animation:pp-pulse 2s ease-in-out infinite; }
@keyframes pp-pulse { 0%,100% { box-shadow:0 2px 8px rgba(245,166,35,.3); } 50% { box-shadow:0 2px 16px rgba(245,166,35,.75); } }
.pp-blk-opts { display:flex; gap:10px; flex-wrap:wrap; }
.pp-blk-opt { padding:11px 16px; border-radius:8px; border:1px solid var(--line); background:#fff; color:var(--navy);
  font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; }
.pp-blk-opt:hover { border-color:var(--amber); background:var(--amber-bg); }
/* The chosen option: filled, so it reads as the answer; the others stay open. */
.pp-blk-opt { display:inline-flex; align-items:center; gap:6px; text-align:left; min-height:44px; touch-action:manipulation; }
.pp-blk-opt.on { border-color:var(--navy); background:var(--navy); color:#fff; cursor:default; }
.pp-blk-opt:disabled { opacity:.6; }
.pp-blk-note { margin-top:6px; font-size:12.5px; color:var(--soft);
  font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; }
.pp-blk-done { display:inline-flex; align-items:center; gap:6px; color:var(--good); font-size:13px; font-weight:700;
  font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; }
.pp-blk-wait { font-size:12.5px; color:var(--faint); font-style:italic;
  font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; }

/* progress */
.pp-progress { position:sticky; top:0; z-index:20; background:var(--card); border:1px solid var(--line);
  border-radius:12px; padding:13px 16px; margin-bottom:14px; box-shadow:0 2px 10px rgba(22,35,61,.07); }
.pp-progress-top { display:flex; align-items:center; justify-content:space-between; gap:10px 12px; flex-wrap:wrap; font-size:14px; }
/* Place-in-batch above the item count: they answer different questions and
   reading as one line ("Document 2 of 3 2 of 5 complete") answers neither. */
.pp-progress-meta { display:flex; flex-direction:column; gap:1px; min-width:0; }
.pp-progress-doc { font-size:11px; font-weight:800; letter-spacing:.6px; text-transform:uppercase; color:var(--soft); }
/* The action never shrinks below its text — a primary action that has ellipsed
   its own label is not one. It takes the full width once wrapped, which on a
   phone makes it the widest tap target on the screen. */
.pp-progress-act { flex:0 0 auto; margin-left:auto; }
.pp-btn-sm { flex:0 1 auto; padding:9px 14px; font-size:13.5px; }
/* The consequence of pressing Complete, above Complete. Muted but not faint:
   this is the last thing a signer reads before a binding act, and --faint is
   the colour of things that do not matter. */
.pp-progress-final { font-size:11.5px; line-height:1.45; color:var(--soft); margin-bottom:6px; text-align:right; max-width:34ch; margin-left:auto; }
/* Only ever seen at narrow widths, where the list above it is capped. */
.pp-remaining-more { display:none; margin:5px 0 0; font-size:12.5px; font-weight:700; color:var(--soft); }
.pp-progress-track { height:6px; background:var(--tint); border-radius:99px; margin-bottom:11px; overflow:hidden; }
.pp-progress-fill { height:100%; background:var(--amber); border-radius:99px; transition:width .3s ease; }
.pp-remaining { margin:9px 0 0; padding-left:18px; font-size:12.5px; color:var(--soft); }
.pp-remaining li { margin:1px 0; }

/* consent */
.pp-consent-text { white-space:pre-wrap; font-size:12.5px; color:var(--soft); background:var(--tint);
  border:1px solid var(--line); border-radius:8px; padding:12px 14px; margin:10px 0 8px;
  max-height:120px; overflow:hidden; position:relative; }
.pp-consent-text.open { max-height:none; }
.pp-fineprint { font-size:12px; color:var(--faint); margin-top:10px; }

/* form bits */
.pp-field { display:block; margin:12px 0; }
.pp-field > span { display:block; font-size:12px; font-weight:700; color:var(--soft); margin-bottom:5px; }
.pp-field input, .pp-field textarea { width:100%; padding:11px 12px; border:1px solid var(--line); border-radius:8px;
  font-size:15px; color:var(--ink); background:#fff; font-family:inherit; }
.pp-field input:focus, .pp-field textarea:focus { outline:2px solid var(--amber); outline-offset:1px; }
.pp-link { background:none; border:none; padding:0; color:var(--amber-deep); font-size:13px; font-weight:700;
  cursor:pointer; text-decoration:underline; font-family:inherit; }
.pp-btn-quiet { background:var(--tint); color:var(--soft); }
.pp-icon-btn { background:none; border:none; cursor:pointer; color:var(--faint); padding:0; display:flex; }

/* adopt dialog */
.pp-modal { position:fixed; inset:0; background:rgba(22,35,61,.55); display:flex; align-items:center;
  justify-content:center; padding:18px; z-index:100; }
.pp-modal-box { background:var(--card); border-radius:14px; padding:20px; width:min(460px,100%);
  max-height:100%; overflow-y:auto; box-shadow:0 24px 70px rgba(22,35,61,.4); }
.pp-modal-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:6px; }
.pp-tabs { display:flex; gap:6px; margin:12px 0 10px; }
.pp-tab { flex:1; padding:9px 12px; border-radius:8px; border:1px solid var(--line); background:var(--tint);
  color:var(--soft); font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; }
.pp-tab.on { background:var(--navy); border-color:var(--navy); color:#fff; }
.pp-adopt-preview { border:1px solid var(--line); border-radius:10px; padding:16px; background:var(--tint); }
/* THE SAME FACE THE DOCUMENT USES. This previously named Snell Roundhand,
   Segoe Script and Bradley Hand — Mac and Windows system faces, none of which
   exist on Linux or Android. So the preview showed one of three different
   handwriting styles depending on the device, or plain cursive, while the
   signature that landed in the document was something else again. A signer
   choosing what their signature looks like has to be shown the real thing.

   'BVL Signature' is self-hosted from this origin (see backend/documentStyles,
   which declares the identical @font-face for the document itself) — still no
   third-party host, which was the sound half of the original reasoning. */
.pp-adopt-sig { font-family:var(--script); font-size:30px;
  color:var(--ink); line-height:1.3; word-break:break-word; }
.pp-adopt-cap { font-size:10px; letter-spacing:.6px; text-transform:uppercase; color:var(--faint); margin-top:4px; }
.pp-adopt-row { display:flex; align-items:center; justify-content:space-between; margin-top:8px; }
.pp-sign-done { border-color:var(--good); }

/* "Document 2 of 3" beside the document number. Quiet on purpose: it is
   orientation, not an instruction, and it sits next to a job number the client
   also does not need to act on. */
.pp-batch { display:inline-block; margin-top:6px; font-size:11px; font-weight:800;
  letter-spacing:.6px; text-transform:uppercase; padding:3px 9px; border-radius:999px;
  background:var(--tint); color:var(--soft); }

/* The continuation, separated from the completion message by a rule rather than
   another card — it is part of "you have finished this one", not a new subject. */
.pp-next { margin-top:16px; padding-top:16px; border-top:1px solid var(--line); }
.pp-next .pp-btn { flex:0 1 auto; margin:10px 0; }

/* ---- contractor work order + estimate (one layout, LD and local) ----
   Real tables, so a printed or extracted PDF never runs figures into labels
   ("7,200 lbCONTRACTOR PAY"). Section titles in Title Case, not small caps. */
.pp-wo .pp-sec { text-transform:none; letter-spacing:0; font-size:15px; color:var(--navy); }
.pp-wo-type { display:inline-block; margin-left:8px; font-size:11px; font-weight:800; padding:3px 9px; border-radius:999px; background:#eef1f6; color:var(--navy); vertical-align:middle; }
.pp-wo-when { text-align:right; font-size:13px; color:var(--soft); }
.pp-wo-when b { display:block; font-size:16px; color:var(--navy); }
.pp-grid { width:100%; border-collapse:collapse; font-size:13px; }
.pp-grid th { text-align:left; font-size:11px; color:var(--faint); font-weight:700; padding:6px 8px; border-bottom:1px solid var(--line); }
.pp-grid td { padding:7px 8px; border-bottom:1px solid #eef0f3; vertical-align:top; color:var(--ink); }
.pp-grid .num { text-align:right; font-family:'DM Mono',ui-monospace,monospace; white-space:nowrap; }
.pp-grid th.num { font-family:inherit; }
.pp-grid tr.sub td { color:var(--soft); }
.pp-grid tr.grand td { border-top:2px solid var(--navy); border-bottom:none; font-weight:800; font-size:15px; color:var(--navy); }
.pp-grid.kv th { width:46%; font-size:12px; color:var(--soft); font-weight:600; border-bottom:1px solid #eef0f3; }
.pp-grid.kv td { font-weight:700; color:var(--navy); }
.pp-grid.access th, .pp-grid.access td { text-align:center; }
.pp-grid.access td.alert { background:var(--amber-bg); color:var(--amber-deep); font-weight:700; }
.pp-pagebreak { break-before:page; page-break-before:always; }
.pp-printonly { display:none; }
.pp-grid.row th, .pp-grid.row td { text-align:center; }
.pp-grid.row td { font-weight:700; color:var(--navy); }

/* ---- the post-move feedback page (073) — phone-first ---- */
.fb { max-width:520px; margin:6px auto 0; }
.fb-h1 { font-size:25px; line-height:1.2; color:var(--navy); font-weight:800; margin:0 0 6px; }
.fb-sub { font-size:14px; color:var(--soft); margin:0 0 20px; }
.fb-p { font-size:15.5px; color:var(--ink); margin:0 0 14px; }
.fb-stars { display:flex; justify-content:center; gap:6px; margin:6px 0 4px; }
.fb-star { background:none; border:none; padding:4px; cursor:pointer; font-size:44px; line-height:1; color:#d5d9e0; -webkit-tap-highlight-color:transparent; }
.fb-star.on { color:var(--amber); }
.fb-star:focus-visible { outline:2px solid var(--navy); outline-offset:2px; border-radius:8px; }
.fb-label { text-align:center; min-height:22px; font-size:14px; font-weight:700; color:var(--amber-deep); margin-bottom:18px; }
.fb-hint { font-size:12.5px; color:var(--faint); margin-top:6px; }
.fb-review { background:var(--card); border:1px solid var(--line); border-radius:12px; padding:14px 16px; margin:4px 0 12px; }
.fb-review-stars { color:var(--amber); font-size:20px; letter-spacing:2px; margin-bottom:6px; }
.fb-review-text { font-size:15px; color:var(--ink); white-space:pre-wrap; overflow-wrap:anywhere; }
.fb-sites { display:flex; flex-direction:column; gap:10px; margin:10px 0 14px; }
.fb-site { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 16px; border-radius:10px; border:1px solid var(--line); background:var(--card); text-decoration:none; color:var(--navy); }
.fb-site:hover { background:var(--tint); }
.fb-site-name { font-size:16px; font-weight:800; }
.fb-site-note { font-size:12.5px; color:var(--soft); white-space:nowrap; }
.fb-small { font-size:12.5px; color:var(--faint); text-align:center; margin-top:6px; }
.fb-copied { font-size:13px; color:var(--good); font-weight:700; margin-left:8px; }
@media (max-width:420px) { .fb-star { font-size:40px; padding:2px; } .fb-h1 { font-size:23px; } }

@media print {
  /* The CRM app's dark body showed under the content on the last page of every
     portal-printed PDF (the client estimate has the same rule). White page. */
  html, body { background:#fff !important; }
  .pp-wo .pp-legs { grid-template-columns:1fr 1fr; }
  .pp-doc-contractor .pp-bar, .pp-doc-contractor .pp-foot { display:none !important; }
  .pp-card, .pp-leg, .pp-room { break-inside:avoid; page-break-inside:avoid; }
  .pp-tbl tr { break-inside:avoid; page-break-inside:avoid; }
  .pp-head { break-after:avoid; }
  .pp { background:#fff; }
  .pp-bar { background:#fff; color:var(--navy); border-bottom:2px solid var(--navy); }
  .pp-tel { color:var(--navy); }
  .pp-logo-screen { display:none; }
  .pp-logo-print { display:block; }
  .pp-acts, .pp-noprint { display:none !important; }
  /* The PDF is the DOCUMENT, never the signing furniture around it. */
  .pp-progress, .pp-blk, .pp-blk-wait, .pp-consent, .pp-modal { display:none !important; }
  /* Batch furniture is signing furniture: a printed contract must not carry
     "Document 2 of 3" or a link to the next one. */
  .pp-batch, .pp-next { display:none !important; }
  .pp-card, .pp-leg { box-shadow:none; break-inside:avoid; }
  .pp-main { padding-top:8px; }

  /* CONTRACTOR WORK ORDER (LD and local) — compact print, so a local job's page
     1 (header, client, pick up + delivery, crew and time, client charges) fits
     ONE page and the inventory starts on page 2. Body ~10pt, titles ~11pt,
     nothing below 9pt. The screen is untouched. */
  .pp-main:has(.pp-wo) { padding:0; }
  .pp-wo { font-size:10pt; line-height:1.35; }
  .pp-wo .pp-head { margin-bottom:8px; }
  .pp-wo .pp-kicker { font-size:9pt; }
  .pp-wo .pp-h1 { font-size:16pt; }
  .pp-wo .pp-wo-type { font-size:9pt; padding:1px 7px; }
  .pp-wo .pp-wo-when { font-size:9pt; }
  .pp-wo .pp-wo-when b { font-size:11pt; }
  .pp-wo .pp-card, .pp-wo .pp-leg { padding:8px 10px; margin-bottom:8px; border-radius:6px; }
  .pp-wo .pp-legs { gap:8px; margin-bottom:8px !important; }
  .pp-wo .pp-sec { font-size:11pt; margin:0 0 4px; }
  .pp-wo .pp-when { font-size:10pt; margin-bottom:2px; }
  .pp-wo .pp-addr { font-size:10.5pt; }
  .pp-wo .pp-addr-sub { font-size:9pt; margin-top:0; }
  .pp-wo .pp-grid { font-size:10pt; }
  .pp-wo .pp-grid th { font-size:9pt; padding:2px 6px; }
  .pp-wo .pp-grid td { padding:2px 6px; }
  .pp-wo .pp-grid.access { margin-top:6px !important; }
  .pp-wo .pp-grid tr.grand td { font-size:11pt; }
  .pp-wo .pp-sub { font-size:9pt; line-height:1.3; margin-top:4px !important; }
  .pp-wo ol { font-size:9pt !important; gap:2px !important; }
  /* Crew And Time prints as one row of five (label above value). */
  .pp-wo .pp-printonly { display:table; }
  /* A long client-charges list may run onto page 2 rather than jump there whole. */
  .pp-wo .pp-card.pp-breakable { break-inside:auto; page-break-inside:auto; }
}
`;
